import { LANE_H, OFFDAY_BG, barGeometry, assignLanes, sameDay, isOffDay } from "./ganttUtils";
import { OrderBar } from "./OrderBar";
import { DowntimeBlock } from "./DowntimeBlock";
import { OperatorChips } from "@/components/OperatorChips";
import { MACHINE_STATUS } from "@/lib/constants";

const STATUS_DOT = { active: "bg-emerald-500", idle: "bg-amber-400", downtime: "bg-red-500" };

// Left header cell: line code + live status dot, name, who is running it, capacity.
function LineHeader({ line }) {
  const isLine = line.id !== "none";
  const st = MACHINE_STATUS[line.status];
  return (
    <div className="px-3 py-3 bg-slate-50/50 flex flex-col justify-center min-w-0 gap-0.5">
      <span className="flex items-center gap-1.5 text-xs font-mono-data font-semibold text-slate-700">
        {isLine && (
          <span className={`h-2 w-2 rounded-full shrink-0 ${STATUS_DOT[line.status] || "bg-slate-300"}`} title={st ? `${st.label}${line.auto_downtime ? " (auto)" : ""}` : "Unknown"}
            data-testid={`gantt-line-status-${line.line_code}`} />
        )}
        {line.line_code}
      </span>
      <span className="text-[11px] text-slate-400 truncate">{line.name}</span>
      {isLine && <OperatorChips operators={line.operators} testid={`gantt-line-operators-${line.line_code}`} />}
      {line.max_hourly_capacity > 0 && <span className="text-[10px] text-slate-400 font-mono-data">{line.max_hourly_capacity}/hr</span>}
    </div>
  );
}

export function GanttRow({
  line,
  orders,
  downtimes,
  cols,
  days,
  winStart,
  winEnd,
  workingDays,
  canDrag,
  dragOver,
  activeDrags,
  shortages,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragPresence,
  onSelectOrder,
}) {
  const items = orders
    .map((o) => ({ o, geo: barGeometry(o, winStart, winEnd) }))
    .filter((it) => it.geo && (it.o.line_id || "none") === line.id)
    .sort((a, b) => a.geo.start - b.geo.start);
  const laned = assignLanes(items);
  const laneCount = Math.max(1, ...laned.map((it) => it.lane + 1));
  const rowH = Math.max(64, laneCount * LANE_H + 12);
  const lineDowntimes = line.id === "none" ? [] : downtimes.filter((dt) => dt.machine_id === line.id);

  return (
    <div className="grid border-b border-slate-50 last:border-0" style={{ gridTemplateColumns: `176px 1fr` }}>
      <LineHeader line={line} />
      <div
        className="relative"
        style={{ height: rowH }}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        data-testid={`gantt-row-${line.line_code}`}
      >
        {/* day grid background */}
        <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${days}, 1fr)` }}>
          {cols.map((d, i) => {
            const off = isOffDay(d, workingDays);
            const isDragTarget = dragOver === `${line.id}-${i}`;
            return (
              <div
                key={d.toISOString()}
                className={`border-l border-slate-100 ${isDragTarget ? "drag-over" : sameDay(d, new Date()) ? "bg-blue-50/40" : ""}`}
                style={off && !isDragTarget ? { background: OFFDAY_BG } : undefined}
              />
            );
          })}
        </div>
        {/* downtime windows (shaded blocks) */}
        {lineDowntimes.map((dt) => (
          <DowntimeBlock key={dt.id} downtime={dt} winStart={winStart} winEnd={winEnd} />
        ))}
        {/* duration bars */}
        {laned.map(({ o, geo, lane }) => (
          <OrderBar key={o.id} order={o} geo={geo} lane={lane} canDrag={canDrag} remoteDrag={activeDrags[o.id]} shortage={shortages?.[o.id]} onDragPresence={onDragPresence} onSelect={onSelectOrder} />
        ))}
      </div>
    </div>
  );
}
